import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import {
  fetchLinkedCustomizationsAsync,
  addCustomizationToMealItemAsync,
  removeCustomizationFromMealItemAsync,
} from './mealItemDetailsSlice';
import { fetchMealItemCustomizationsAsync } from './customizations/mealItemCustomizationSlice';
import { LoadingButton } from '@mui/lab';
import { MealItemCustomization } from '../../../app/data/models/mealItems/mealItemCustomization';

interface Props {
  restaurantId: string;
  mealItemId: string;
  onBack: () => void;
}

interface ActionState {
  id: string;
  loading: boolean;
}

const MealItemLinkedCustomizations = ({
  restaurantId,
  mealItemId,
  onBack,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { linkedCustomizations } = useAppSelector(
    (state) => state.mealItemDetails
  );
  const { mealItemCustomizations } = useAppSelector(
    (state) => state.mealItemCustomizations
  );
  const [actionStates, setActionStates] = useState<ActionState[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchMealItemCustomizationsAsync({ restaurantId }));
    dispatch(fetchLinkedCustomizationsAsync({ restaurantId, mealItemId }));
  }, [dispatch, restaurantId, mealItemId]);

  const handleAction = async (
    customization: MealItemCustomization,
    isLinking: boolean
  ) => {
    setActionStates((prev) => [
      ...prev,
      { id: customization.id, loading: true },
    ]);

    try {
      await dispatch(
        isLinking
          ? addCustomizationToMealItemAsync({
              restaurantId,
              mealItemId,
              customizationId: customization.id,
            })
          : removeCustomizationFromMealItemAsync({
              restaurantId,
              mealItemId,
              customizationId: customization.id,
            })
      ).unwrap();
    } catch {
      setErrorMessage(
        t(
          isLinking
            ? 'meal-item.link-error-specific'
            : 'meal-item.unlink-error-specific',
          { name: customization.customerInstruction }
        )
      );
    } finally {
      setActionStates((prev) =>
        prev.filter((state) => state.id !== customization.id)
      );
    }
  };

  // Filter out already linked customizations
  const availableCustomizations = mealItemCustomizations.filter(
    (customization) =>
      !linkedCustomizations.some((linked) => linked.id === customization.id)
  );

  return (
    <Box>
      <ListTitleBox
        title={t('meal-item.customizations.title')}
        onBackClick={onBack}
      />
      <Box
        component={Paper}
        elevation={2}
        sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%', mb: 2 }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('meal-item.customizations.linked')}
          </Typography>
          {linkedCustomizations.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ textAlign: 'center', py: 2 }}
            >
              {t('meal-item.customizations.no-linked')}
            </Typography>
          ) : (
            <List>
              {linkedCustomizations.map((customization) => {
                const isLoading = actionStates.some(
                  (state) => state.id === customization.id
                );

                return (
                  <ListItem
                    key={customization.id}
                    secondaryAction={
                      <LoadingButton
                        sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                        loading={isLoading}
                        onClick={() => handleAction(customization, false)}
                        color="error"
                        size="small"
                        disabled={isLoading}
                      >
                        <Delete />
                      </LoadingButton>
                    }
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <ListItemText
                      primary={customization.customerInstruction}
                      secondary={t('meal-item.options-count', {
                        count: customization.options.length,
                      })}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('meal-item.customizations.available')}
          </Typography>
          {mealItemCustomizations.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ textAlign: 'center', py: 2 }}
            >
              {t('meal-item.customizations.no-available')}
            </Typography>
          ) : (
            <List>
              {availableCustomizations.map((customization) => {
                const isLoading = actionStates.some(
                  (state) => state.id === customization.id
                );

                return (
                  <ListItem
                    key={customization.id}
                    secondaryAction={
                      <LoadingButton
                        sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                        loading={isLoading}
                        onClick={() => handleAction(customization, true)}
                        color="primary"
                        size="small"
                        disabled={isLoading}
                      >
                        <Add />
                      </LoadingButton>
                    }
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <ListItemText
                      primary={customization.customerInstruction}
                      secondary={t('meal-item.options-count', {
                        count: customization.options.length,
                      })}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </Box>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          variant="filled"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MealItemLinkedCustomizations;
